<template>
  <transition name="fade">
    <Loader v-if="loading" />
    <div class="home flex" v-else>
      <side-menu />
      <div class="lg:pl-16 flex-grow w-full min-h-screen">
        <NavBar title="OpenClaw" />
        <div class="flex flex-col w-full p-8 space-y-4 md:w-10/12 lg:w-8/12 xl:w-6/12">

          <!-- Coming Soon Banner -->
          <div class="bg-bs-blue-dark rounded-3xl p-6 xl:p-8 text-center">
            <div class="text-4xl mb-4">&#x1F916;</div>
            <h2 class="text-2xl font-bold text-white mb-3">Coming Soon</h2>
            <p class="text-white text-opacity-80 text-sm max-w-md mx-auto mb-4">
              We're building an OpenClaw integration that will place your bets automatically
              in the background — even while you sleep. Using AI-powered browser automation,
              tips will be placed on your behalf the moment they fire.
            </p>
            <p class="text-white text-opacity-50 text-xs">
              Stay tuned — we'll notify you when it's ready.
            </p>
          </div>

          <!-- Section 1: API Key (hidden for now) -->
          <div class="bg-white dark:bg-bs-dark-bg-over rounded-3xl tip-item p-3 xl:p-5" style="display:none;">
            <h3 class="text-lg font-bold text-bs-dark-bg dark:text-white mb-3">Connect OpenClaw</h3>

            <!-- No key yet -->
            <div v-if="!hasKey && !generatedKey">
              <p class="text-sm text-bs-gray-dark dark:text-bs-gray mb-4">
                Generate an API key to connect your OpenClaw instance with Betslip Tips.
                This allows OpenClaw to fetch your tips and place bets on your behalf.
              </p>
              <button
                class="btn-primary h-10 px-6"
                @click="onGenerateKey"
              >
                Generate API Key
              </button>
            </div>

            <!-- Key just generated (show once) -->
            <div v-if="generatedKey">
              <div class="bg-bs-blue-dark text-white rounded-xl p-4 mb-3">
                <p class="text-xs text-white text-opacity-60 mb-1">Your API Key (save this — it won't be shown again)</p>
                <div class="flex items-center gap-2">
                  <code class="text-sm break-all flex-grow">{{ generatedKey }}</code>
                  <button
                    @click="copyKey"
                    class="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg px-3 py-1 text-xs whitespace-nowrap"
                  >
                    {{ copied ? 'Copied!' : 'Copy' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Existing key -->
            <div v-if="hasKey && !generatedKey">
              <div class="flex items-center justify-between mb-3">
                <div>
                  <p class="text-sm text-bs-gray-dark dark:text-bs-gray">
                    Active key: <code class="text-bs-blue-dark dark:text-white font-mono">{{ keyPrefix }}...</code>
                  </p>
                  <p class="text-xs text-bs-gray mt-1">Created: {{ keyCreatedDate }}</p>
                </div>
                <button
                  class="text-red-500 text-sm underline hover:text-red-400"
                  @click="onRevokeKey"
                >
                  Revoke
                </button>
              </div>
            </div>
          </div>

          <!-- Section 2: OpenClaw Status -->
          <div class="bg-white dark:bg-bs-dark-bg-over rounded-3xl tip-item p-3 xl:p-5" v-if="hasKey">
            <div class="flex items-center justify-between mb-3">
              <h3 class="text-lg font-bold text-bs-dark-bg dark:text-white">OpenClaw Status</h3>
              <div v-if="statusConnected" class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-bs-green inline-block"></span>
                <span class="text-xs text-bs-gray">Last seen: {{ statusLastSeen }}</span>
              </div>
              <div v-else class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-gray-400 inline-block"></span>
                <span class="text-xs text-bs-gray">Not connected</span>
              </div>
            </div>

            <div v-if="statusConfig" class="overflow-hidden rounded-xl border border-gray-200 dark:border-bs-blue-dark">
              <table class="w-full text-sm">
                <tbody>
                  <tr v-for="(row, idx) in configRows" :key="idx"
                    :class="idx < configRows.length - 1 ? 'border-b border-gray-100 dark:border-bs-blue-dark' : ''">
                    <td class="px-4 py-2.5 text-bs-gray-dark dark:text-bs-gray font-medium">{{ row.label }}</td>
                    <td class="px-4 py-2.5" :class="row.colorClass || 'text-bs-dark-bg dark:text-white'">{{ row.value }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p v-if="!statusConnected" class="text-xs text-bs-gray mt-3">
              OpenClaw will appear here once you run the skill for the first time.
            </p>
          </div>

          <!-- Section 3: Important Notice -->
          <div class="bg-red-900 bg-opacity-20 border border-red-500 border-opacity-30 rounded-3xl p-3 xl:p-5">
            <h3 class="text-lg font-bold text-red-400 mb-2">Important: Use Your Home Network</h3>
            <p class="text-sm text-bs-gray-dark dark:text-bs-gray">
              Bet365 actively blocks server IPs, VPNs, and datacenter connections. To avoid account restrictions,
              <strong class="text-white">always run OpenClaw on your home computer using your residential internet connection</strong> —
              the same network you normally use to access Bet365. Never run this on a VPS, cloud server, or via a VPN.
            </p>
          </div>

          <!-- Section 4: Setup Instructions -->
          <div class="bg-white dark:bg-bs-dark-bg-over rounded-3xl tip-item p-3 xl:p-5">
            <h3 class="text-lg font-bold text-bs-dark-bg dark:text-white mb-3">Setup Instructions</h3>
            <div class="space-y-4">
              <div class="flex gap-3">
                <span class="bg-bs-blue-dark text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                <div>
                  <p class="text-sm font-medium text-bs-dark-bg dark:text-white">Install OpenClaw on your home computer</p>
                  <p class="text-xs text-bs-gray mt-1">
                    Visit <a href="https://openclaw.ai" target="_blank" class="text-bs-blue-dark dark:text-blue-400 underline">openclaw.ai</a>
                    to install OpenClaw. Make sure it's running on your personal machine with a residential internet connection.
                  </p>
                </div>
              </div>
              <div class="flex gap-3">
                <span class="bg-bs-blue-dark text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                <div>
                  <p class="text-sm font-medium text-bs-dark-bg dark:text-white">Install the Betslip Tips skill</p>
                  <code class="text-xs bg-gray-100 dark:bg-bs-dark-bg rounded px-2 py-1 mt-1 block text-bs-gray-dark dark:text-bs-gray">openclaw skill install betslip-tips</code>
                </div>
              </div>
              <div class="flex gap-3">
                <span class="bg-bs-blue-dark text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                <div>
                  <p class="text-sm font-medium text-bs-dark-bg dark:text-white">Generate your API key above and set it</p>
                  <code class="text-xs bg-gray-100 dark:bg-bs-dark-bg rounded px-2 py-1 mt-1 block text-bs-gray-dark dark:text-bs-gray break-all">export BETSLIP_API_TOKEN={{ generatedKey || keyPrefix + '...' || 'your-api-key' }}</code>
                </div>
              </div>
              <div class="flex gap-3">
                <span class="bg-bs-blue-dark text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                <div>
                  <p class="text-sm font-medium text-bs-dark-bg dark:text-white">Run the setup wizard</p>
                  <code class="text-xs bg-gray-100 dark:bg-bs-dark-bg rounded px-2 py-1 mt-1 block text-bs-gray-dark dark:text-bs-gray">/betslip-tips setup</code>
                  <p class="text-xs text-bs-gray mt-1">
                    The wizard walks you through configuring your stake, currency, daily limits, and Bet365 credentials.
                    All settings and credentials stay on your machine — nothing is sent to our servers except a status snapshot
                    (without passwords) so you can monitor the connection here.
                  </p>
                </div>
              </div>
              <div class="flex gap-3">
                <span class="bg-bs-blue-dark text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
                <div>
                  <p class="text-sm font-medium text-bs-dark-bg dark:text-white">That's it — OpenClaw takes over</p>
                  <p class="text-xs text-bs-gray mt-1">
                    Once setup is complete, just say <strong class="text-white">"start betslip tips"</strong> and OpenClaw
                    runs autonomously — it polls for new tips, logs into Bet365, and places bets automatically.
                    You can check progress anytime with <code class="bg-gray-100 dark:bg-bs-dark-bg rounded px-1">/betslip-tips status</code>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 3: How It Works -->
          <div class="bg-white dark:bg-bs-dark-bg-over rounded-3xl tip-item p-3 xl:p-5">
            <h3 class="text-lg font-bold text-bs-dark-bg dark:text-white mb-3">How It Works</h3>
            <div class="space-y-3 text-sm text-bs-gray-dark dark:text-bs-gray">
              <div class="flex items-start gap-3">
                <span class="text-lg">&#x1F4CA;</span>
                <p>Our algorithms analyse live match data and generate in-play tips when profitable conditions are detected.</p>
              </div>
              <div class="flex items-start gap-3">
                <span class="text-lg">&#x1F916;</span>
                <p>OpenClaw runs autonomously on your machine — it polls for new tips, logs into Bet365, and places bets automatically when signals arrive.</p>
              </div>
              <div class="flex items-start gap-3">
                <span class="text-lg">&#x2705;</span>
                <p>Built-in safety: daily loss caps, max bets per day, and optional manual confirmation mode if you want to approve each bet.</p>
              </div>
              <div class="flex items-start gap-3">
                <span class="text-lg">&#x1F512;</span>
                <p>Your Bet365 credentials never leave your machine. They are stored locally in OpenClaw's config, not on our servers.</p>
              </div>
            </div>
          </div>

        </div>
      </div>
      <mob-menu class="lg:hidden" />
    </div>
  </transition>
</template>

<script>
import NavBar from "@/components/organisms/NavBar.vue";
import SideMenu from "@/components/organisms/SideMenu.vue";
import Loader from "@/components/atoms/Loader.vue";
import MobMenu from "@/components/organisms/MobMenu.vue";
import format from "date-fns/esm/format";

export default {
  components: {
    SideMenu,
    NavBar,
    Loader,
    MobMenu,
  },
  name: "OpenClaw",
  data: function () {
    return {
      loading: false,
      hasKey: false,
      keyPrefix: "",
      keyCreatedDate: "",
      generatedKey: null,
      copied: false,
      statusConnected: false,
      statusLastSeen: "",
      statusConfig: null,
    };
  },
  computed: {
    configRows: function () {
      if (!this.statusConfig) return [];
      var c = this.statusConfig;
      var labelMap = {
        stakeAmount: { label: 'Stake Amount', format: v => (c.stakeCurrency || 'EUR') + ' ' + v + ' per bet' },
        stakeCurrency: null,
        maxBetsPerDay: { label: 'Max Bets/Day' },
        autoMode: { label: 'Auto-mode', format: v => v ? 'ON (no confirmation)' : 'OFF (manual confirmation)' },
        dailyLossCap: { label: 'Daily Loss Cap', format: v => (c.stakeCurrency || 'EUR') + ' ' + v },
        dryRun: { label: 'Dry-run', format: v => v ? 'ON (simulated bets)' : 'OFF (real bets)' },
        bet365Login: {
          label: 'Bet365 Login',
          format: v => v === 'configured' ? 'Configured' : 'Not configured',
          colorClass: v => v === 'configured' ? 'text-bs-green' : 'text-red-400'
        },
      };
      var rows = [];
      for (var key in c) {
        var mapping = labelMap[key];
        if (mapping === null) continue;
        if (mapping === undefined) {
          mapping = { label: key };
        }
        var val = c[key];
        rows.push({
          label: mapping.label || key,
          value: mapping.format ? mapping.format(val) : String(val),
          colorClass: mapping.colorClass ? mapping.colorClass(val) : 'text-bs-dark-bg dark:text-white',
        });
      }
      return rows;
    },
  },
  created: async function () {
    this.loading = true;
    await this.fetchKeyStatus();
    this.loading = false;
  },
  methods: {
    fetchKeyStatus: async function () {
      try {
        const res = await this.$store.dispatch("getApiKey");
        if (res.data.hasKey) {
          this.hasKey = true;
          this.keyPrefix = res.data.prefix;
          this.keyCreatedDate = format(new Date(res.data.createdDate), "dd-MM-yyyy HH:mm");
          await this.fetchStatus();
        }
      } catch (e) {
        console.log("Failed to fetch API key status", e);
      }
    },
    fetchStatus: async function () {
      try {
        const res = await this.$store.dispatch("getOpenClawStatus");
        if (res.data.connected) {
          this.statusConnected = true;
          this.statusLastSeen = format(new Date(res.data.lastHeartbeat), "dd-MM-yyyy HH:mm");
          this.statusConfig = res.data.config;
        }
      } catch (e) {
        console.log("Failed to fetch status", e);
      }
    },
    onGenerateKey: async function () {
      try {
        const res = await this.$store.dispatch("generateApiKey");
        this.generatedKey = res.data.key;
        this.hasKey = true;
        this.keyPrefix = res.data.prefix;
        this.keyCreatedDate = format(new Date(res.data.createdDate), "dd-MM-yyyy HH:mm");
      } catch (e) {
        console.log("Failed to generate API key", e);
      }
    },
    onRevokeKey: async function () {
      try {
        await this.$store.dispatch("revokeApiKey");
        this.hasKey = false;
        this.keyPrefix = "";
        this.keyCreatedDate = "";
        this.generatedKey = null;
      } catch (e) {
        console.log("Failed to revoke API key", e);
      }
    },
    copyKey: function () {
      navigator.clipboard.writeText(this.generatedKey);
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
  },
};
</script>
